import { displayDateUS } from "../../utils/dateUtils";

/** @param {unknown} value */
function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(val) {
  if (!val) return "";
  const s = displayDateUS(val);
  return s || String(val);
}

function isTruthy(v) {
  if (v === true || v === 1) return true;
  if (v === false || v == null) return false;
  const s = String(v).toLowerCase();
  return s === "true" || s === "1" || s === "yes";
}

/**
 * Matches parsing logic in HepatitisBPolicy.jsx
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parseHepatitisBPolicyFromApiResponse(response, targetUserId) {
  if (!response || response.status !== 1) return null;
  const { result } = response;
  if (Array.isArray(result)) {
    return result.length > 0 ? result[0] : null;
  }
  if (result?.policy) {
    return result.policy;
  }
  if (result?.items && Array.isArray(result.items)) {
    const uid = parseInt(String(targetUserId), 10);
    return result.items.find((item) => item.userId === uid) || null;
  }
  if (
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result.id != null ||
      result.employeeName != null ||
      result.userId != null)
  ) {
    return result;
  }
  return result || null;
}

function signatureBlock(sig) {
  if (sig == null || sig === "") {
    return `<span class="sig-empty">—</span>`;
  }
  const s = String(sig);
  if (s.startsWith("data:")) {
    return `<img class="sig-img" src="${escapeHtml(s)}" alt="" />`;
  }
  return `<span class="sig-text">${escapeHtml(s)}</span>`;
}

/**
 * Copy aligned with HepatitisBPolicy.jsx (no applicant certification block).
 * @param {Record<string, unknown>} record
 * @param {{ companyName: string; logoUrl: string }} branding
 */
export function buildHepatitisBPolicyPrintHtml(record, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");

  const employeeName = escapeHtml(
    String(record.employeeName || record.applicantName || "").trim(),
  );
  const title = escapeHtml(String(record.employeeTitle || record.title || ""));
  const position = escapeHtml(
    String(record.positionAppliedFor || record.position || ""),
  );
  const ackDate = escapeHtml(formatDate(record.acknowledgementDate));

  const declining = isTruthy(record.isDecliningVaccination);
  const vaccinated = isTruthy(record.hasBeenVaccinated);

  const es = record.employeeSignature;
  const nestedEmpSig =
    es && typeof es === "object"
      ? /** @type {{ signature?: string }} */ (es).signature
      : null;
  const applicantSig =
    record.applicantSignature ||
    record.empSignature ||
    nestedEmpSig;
  const applicantSignedAt = escapeHtml(
    formatDate(
      record.applicantSignedAt ||
        record.applicantSignatureDate ||
        record.empSignedAt,
    ),
  );
  const applicantSignedBy = escapeHtml(
    String(record.applicantSignedBy || "").trim(),
  );

  const reviewedBySignedBy = escapeHtml(
    String(record.reviewedBySignedBy || "").trim(),
  );
  const reviewedBySig =
    record.reviewedBySignature || record.adminSignature;
  const reviewedBySignedAt = escapeHtml(
    formatDate(
      record.reviewedBySignedAt ||
        record.adminSignatureDate ||
        record.reviewedBySignatureDate,
    ),
  );

  const displayName = employeeName || applicantSignedBy || "—";

  const chkDecl = declining
    ? `<span class="chk on" aria-hidden="true">✓</span>`
    : `<span class="chk" aria-hidden="true"></span>`;
  const chkVac = vaccinated
    ? `<span class="chk on" aria-hidden="true">✓</span>`
    : `<span class="chk" aria-hidden="true"></span>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Hepatitis B vaccination policy — ${employeeName || "Employee"}</title>
  <style>
    @page { margin: 0.4in; size: letter; }
    * { box-sizing: border-box; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 11px;
      line-height: 1.45;
      color: #111827;
      margin: 0;
      padding: 12px;
    }
    .sheet { max-width: 880px; margin: 0 auto; }
    @media screen {
      html {
        background: linear-gradient(168deg, #cbd5e1 0%, #e2e8f0 42%, #f1f5f9 100%);
        min-height: 100%;
      }
      body {
        padding: 28px 18px 40px;
        background: transparent;
        min-height: 100vh;
      }
      .sheet {
        background: #ffffff;
        padding: 32px 36px 40px;
        border: 1px solid #e2e8f0;
        border-radius: 1px;
        box-shadow:
          0 1px 2px rgba(15, 23, 42, 0.05),
          0 24px 48px -16px rgba(15, 23, 42, 0.22);
      }
    }
    @media print {
      html { background: #fff !important; }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        padding: 0 !important;
        margin: 0;
        background: #fff !important;
      }
      .sheet {
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        background: #fff !important;
      }
    }
    .top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      padding-bottom: 12px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 16px;
    }
    .logo { height: 56px; width: auto; max-width: 200px; object-fit: contain; }
    .title-block { text-align: right; flex: 1; }
    .title-main { font-size: 16px; font-weight: 700; color: #111827; margin: 0 0 4px 0; }
    .title-sub { font-size: 13px; font-weight: 500; color: #2563eb; margin: 0; }
    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px 20px;
      margin-bottom: 18px;
    }
    .field-label {
      font-size: 10px;
      font-weight: 600;
      color: #6b7280;
      margin-bottom: 4px;
    }
    .field-value {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 8px 10px;
      min-height: 38px;
      background: #fff;
      font-size: 12px;
      color: #111827;
    }
    .auth-box {
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 16px;
      margin-bottom: 14px;
    }
    .auth-box p {
      margin: 0 0 12px 0;
      text-align: justify;
      font-size: 11px;
      color: #374151;
    }
    .auth-box p:last-child { margin-bottom: 0; }
    .divider {
      height: 0;
      border: none;
      border-top: 2px solid #cbd5e1;
      margin: 16px 0;
    }
    .opts { margin: 14px 0 18px; }
    .opt-row {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      margin-bottom: 10px;
      font-size: 11px;
      color: #1e293b;
    }
    .chk {
      width: 16px;
      height: 16px;
      border: 2px solid #374151;
      border-radius: 3px;
      flex-shrink: 0;
      margin-top: 1px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      font-weight: 700;
      color: #fff;
      background: #fff;
    }
    .chk.on {
      background: #111827;
      border-color: #111827;
    }
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      margin-top: 20px;
    }
    .sig-card {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px;
      background: #fafafa;
    }
    .sig-card h3 {
      font-size: 12px;
      font-weight: 700;
      margin: 0 0 10px 0;
      color: #111827;
    }
    .sig-label {
      font-size: 9px;
      font-weight: 600;
      letter-spacing: 0.06em;
      color: #6b7280;
      margin: 8px 0 4px 0;
    }
    .sig-line {
      border-bottom: 1px solid #9ca3af;
      min-height: 28px;
      font-size: 12px;
      padding: 4px 0;
    }
    .sig-img { max-height: 48px; max-width: 100%; object-fit: contain; }
    .sig-empty { color: #9ca3af; }
    .footer-print {
      margin-top: 24px;
      padding-top: 12px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 10px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="title-block">
        <p class="title-main">Hepatitis B vaccination policy</p>
        <p class="title-sub">${company}</p>
      </div>
    </div>

    <div class="grid">
      <div>
        <div class="field-label">Employee name</div>
        <div class="field-value">${employeeName || "—"}</div>
      </div>
      <div>
        <div class="field-label">Title</div>
        <div class="field-value">${title || "—"}</div>
      </div>
      <div>
        <div class="field-label">Position applied for</div>
        <div class="field-value">${position || "—"}</div>
      </div>
      <div>
        <div class="field-label">Acknowledgement date</div>
        <div class="field-value">${ackDate || "—"}</div>
      </div>
    </div>

    <div class="auth-box">
      <p>
        The Center of Disease Control (CDC) and Occupational Safety and
        Health Administration (OSHA) recommend immunization for all
        healthcare workers in the high-risk category. As healthcare
        personnel who will be exposed to the patients' blood and body
        fluid, you will fall into this high-risk category.
      </p>
      <p>
        The CDC Immunization Practices Advisory Committee recommends
        that if you are NOT vaccinated, you should receive one dose of
        Hepatitis Immune Globulin Human (H BIG) and begin a series of
        Hepatitis B Virus (HBV) vaccine.
      </p>
    </div>

    <hr class="divider" />

    <div class="auth-box">
      <p>
        I, the undersigned, have been offered the opportunity to receive
        a Hepatitis B Vaccination, free of charge as an employment
        benefit provided by our Agency.
      </p>
      <p>
        I have read the above statement and I am aware that if
        unvaccinated, I am at risk of contracting Hepatitis B during
        employment.
      </p>
    </div>

    <div class="opts">
      <div class="opt-row">${chkDecl}<span>I am declining to receive the vaccination at this time.</span></div>
      <div class="opt-row">${chkVac}<span>I have already been vaccinated against the Hepatitis B virus.</span></div>
    </div>

    <div class="two-col">
      <div class="sig-card">
        <h3>Employee</h3>
        <div class="sig-label">Name</div>
        <div class="field-value">${displayName}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(applicantSig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${applicantSignedAt || "—"}</div>
      </div>
      <div class="sig-card">
        <h3>Admin / reviewer</h3>
        <div class="sig-label">Name</div>
        <div class="field-value">${reviewedBySignedBy || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(reviewedBySig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${reviewedBySignedAt || "—"}</div>
      </div>
    </div>

    <div class="footer-print">${company}</div>
  </div>
</body>
</html>`;
}
